import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("admin-token")}` });
const STATUS = ["pending", "processing", "shipped", "delivered", "cancelled"];

export default function Orders() {
  const [orders, setOrders]     = useState([]);
  const [expanded, setExpanded] = useState(null); // which row is expanded

  const load = () =>
    axios.get(`${API}/admin/orders`, { headers: authHeader() }).then(r => setOrders(r.data));

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, orderStatus) => {
    await axios.put(`${API}/admin/orders/${id}`, { orderStatus }, { headers: authHeader() });
    toast.success("Status updated!");
    load();
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success("Tracking token copied!");
  };

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>Orders</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 24 }}>
        Click any row to expand full details including tracking token.
      </p>

      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f3f4f6" }}>
            <tr>
              {["Order ID", "Customer", "Phone", "Total", "Payment", "Order Status", "Update Status"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <>
                {/* Main Row */}
                <tr
                  key={o._id}
                  onClick={() => setExpanded(expanded === o._id ? null : o._id)}
                  style={{
                    borderBottom: "1px solid #f3f4f6",
                    cursor: "pointer",
                    background: expanded === o._id ? "#faf5ff" : "white",
                    transition: "background 0.15s",
                  }}
                >
                  <td style={tdStyle}>
                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                      #{o._id.slice(-6).toUpperCase()}
                    </span>
                  </td>

                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{o.user?.name || "Guest"}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{o.guestEmail || o.user?.email || "—"}</div>
                  </td>

                  <td style={tdStyle}>
                    <span style={{ fontFamily: "monospace", fontSize: 13 }}>
                      {o.phone || "—"}
                    </span>
                  </td>

                  <td style={tdStyle}>
                    <strong>₹{o.total}</strong>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {o.paymentMethod === "cod"           && "💵 COD"}
                      {o.paymentMethod === "phone_confirm" && "📞 Phone"}
                      {o.paymentMethod === "online"        && "💳 Online"}
                    </div>
                  </td>

                  <td style={tdStyle}>
                    <span style={{
                      padding: "4px 10px", borderRadius: 20, fontSize: 12,
                      background: o.paymentStatus === "paid"                  ? "#d1fae5"
                                : o.paymentStatus === "awaiting_confirmation" ? "#fef3c7"
                                : o.paymentStatus === "failed"                ? "#fee2e2"
                                : "#f3f4f6",
                      color:     o.paymentStatus === "paid"                  ? "#065f46"
                                : o.paymentStatus === "awaiting_confirmation" ? "#92400e"
                                : o.paymentStatus === "failed"                ? "#991b1b"
                                : "#374151",
                    }}>
                      {o.paymentStatus === "awaiting_confirmation" ? "awaiting" : o.paymentStatus}
                    </span>
                  </td>

                  <td style={tdStyle}>
                    <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, background: "#e0e7ff", color: "#3730a3" }}>
                      {o.orderStatus}
                    </span>
                  </td>

                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <select
                      value={o.orderStatus}
                      onChange={e => updateStatus(o._id, e.target.value)}
                      style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}
                    >
                      {STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>

                {/* Expanded Detail Row */}
                {expanded === o._id && (
                  <tr key={`${o._id}-expanded`}>
                    <td colSpan={7} style={{ background: "#faf5ff", padding: "0 16px 16px 16px", borderBottom: "2px solid #e9d5ff" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, paddingTop: 16 }}>

                        {/* Tracking Token */}
                        <div style={cardStyle}>
                          <p style={cardLabel}>📦 Tracking Token</p>
                          <p style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", color: "#4c1d95", marginBottom: 8 }}>
                            {o.trackingToken || "—"}
                          </p>
                          {o.trackingToken && (
                            <button onClick={() => copyToken(o.trackingToken)} style={smallBtnStyle}>
                              Copy Token
                            </button>
                          )}
                        </div>

                        {/* Shipping Address */}
                        <div style={cardStyle}>
                          <p style={cardLabel}>📍 Shipping Address</p>
                          <p style={{ fontSize: 13, lineHeight: 1.6, color: "#374151" }}>
                            {o.shippingAddress?.firstName} {o.shippingAddress?.lastName}<br />
                            {o.shippingAddress?.address}<br />
                            {o.shippingAddress?.city}, {o.shippingAddress?.state} {o.shippingAddress?.zip}
                          </p>
                        </div>

                        {/* Order Items */}
                        <div style={cardStyle}>
                          <p style={cardLabel}>🛍 Items ({o.items?.length})</p>
                          {o.items?.map((item, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                              <span>{item.name} × {item.quantity}</span>
                              <span style={{ fontWeight: 600 }}>₹{item.price * item.quantity}</span>
                            </div>
                          ))}
                          <div style={{ borderTop: "1px solid #e9d5ff", marginTop: 8, paddingTop: 8, fontSize: 13 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
                              <span>Subtotal</span><span>₹{o.subtotal}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
                              <span>Shipping</span><span>{o.shipping === 0 ? "Free" : `₹${o.shipping}`}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>
                              <span>Tax</span><span>₹{o.tax}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 4 }}>
                              <span>Total</span><span>₹{o.total}</span>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Phone confirm note */}
                      {o.paymentMethod === "phone_confirm" && o.paymentStatus !== "paid" && (
                        <div style={{ marginTop: 12, padding: "10px 14px", background: "#fef3c7", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
                          📞 <strong>Action needed:</strong> Call customer at <strong>{o.phone}</strong> to confirm payment. Share tracking token after confirmation.
                        </div>
                      )}
                      {o.paymentMethod === "cod" && (
                        <div style={{ marginTop: 12, padding: "10px 14px", background: "#ecfdf5", borderRadius: 8, fontSize: 13, color: "#065f46" }}>
                          💵 <strong>COD Order:</strong> Collect ₹{o.total} at delivery from {o.shippingAddress?.firstName}.
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
                  No orders yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle  = { padding: "12px 16px", textAlign: "left", fontSize: 13, fontWeight: 600, color: "#374151" };
const tdStyle  = { padding: "12px 16px", fontSize: 14, color: "#374151", verticalAlign: "middle" };
const cardStyle = { background: "#fff", borderRadius: 8, padding: 12, border: "1px solid #e9d5ff" };
const cardLabel = { fontSize: 12, fontWeight: 700, color: "#7c3aed", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" };
const smallBtnStyle = { padding: "5px 12px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" };